import React, { useState, useEffect } from 'react';
import { Save, Trash2, Download, Upload, Star, Copy } from 'lucide-react';
import { useNotifications } from './NotificationSystem';
import apiService from '../services/api';

const TemplateManager = ({ nodes, onLoadTemplate, onClose }) => {
  const { showSuccess, showError, showWarning } = useNotifications();
  const [templates, setTemplates] = useState([]);
  const [selectedNodes, setSelectedNodes] = useState([]);
  const [templateName, setTemplateName] = useState('');
  const [templateDescription, setTemplateDescription] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
    try {
      const templateList = await apiService.getTemplates();
      setTemplates(templateList);
    } catch (error) {
      console.error('Failed to load templates:', error);
    }
  };

  const saveTemplate = async () => {
    if (!templateName.trim()) {
      showError('Please enter a template name', 'Validation Error');
      return;
    }

    if (selectedNodes.length === 0) {
      showError('Please select at least one node', 'Validation Error');
      return;
    }

    setLoading(true);
    try {
      const templateNodes = nodes.filter(node => selectedNodes.includes(node.id));
      const template = {
        name: templateName,
        description: templateDescription,
        nodes: templateNodes,
        node_count: templateNodes.length,
        tags: ['custom'], // Could be expanded
      };

      await apiService.saveTemplate(template);
      await loadTemplates();
      
      setTemplateName('');
      setTemplateDescription('');
      setSelectedNodes([]);
      
      showSuccess('Template saved successfully!', 'Template Saved');
    } catch (error) {
      console.error('Failed to save template:', error);
      showError('Failed to save template. Please try again.', 'Save Error');
    } finally {
      setLoading(false);
    }
  };

  const deleteTemplate = async (templateId) => {
    if (!confirm('Are you sure you want to delete this template?')) {
      return;
    }

    try {
      await apiService.deleteTemplate(templateId);
      await loadTemplates();
      showSuccess('Template deleted successfully!', 'Template Deleted');
    } catch (error) {
      console.error('Failed to delete template:', error);
      showError('Failed to delete template. Please try again.', 'Delete Error');
    }
  };

  const loadTemplate = (template) => {
    onLoadTemplate(template.nodes);
    onClose();
  };

  const toggleNodeSelection = (nodeId) => {
    setSelectedNodes(prev => 
      prev.includes(nodeId) 
        ? prev.filter(id => id !== nodeId)
        : [...prev, nodeId]
    );
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl mx-4 max-h-[90vh] overflow-hidden">
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-800">
            Template Manager
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            ×
          </button>
        </div>

        <div className="flex h-[70vh]">
          {/* Create Template Section */}
          <div className="w-1/2 p-4 border-r border-gray-200">
            <h4 className="font-medium text-gray-800 mb-4">Create New Template</h4>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Template Name
                </label>
                <input
                  type="text"
                  value={templateName}
                  onChange={(e) => setTemplateName(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Enter template name..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description
                </label>
                <textarea
                  value={templateDescription}
                  onChange={(e) => setTemplateDescription(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent h-20 resize-none"
                  placeholder="Describe what this template does..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select Nodes ({selectedNodes.length} selected)
                </label>
                <div className="max-h-40 overflow-y-auto border border-gray-200 rounded-lg p-2 space-y-1">
                  {nodes.map(node => (
                    <label key={node.id} className="flex items-center gap-2 p-2 hover:bg-gray-50 rounded cursor-pointer">
                      <input
                        type="checkbox"
                        checked={selectedNodes.includes(node.id)}
                        onChange={() => toggleNodeSelection(node.id)}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="text-sm text-gray-700">
                        {node.data.label || `${node.type} node`}
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              <button
                onClick={saveTemplate}
                disabled={loading || !templateName.trim() || selectedNodes.length === 0}
                className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
              >
                <Save size={16} />
                {loading ? 'Saving...' : 'Save Template'}
              </button>
            </div>
          </div>

          {/* Templates List Section */}
          <div className="w-1/2 p-4">
            <h4 className="font-medium text-gray-800 mb-4">Saved Templates</h4>
            
            <div className="space-y-2 max-h-full overflow-y-auto">
              {templates.length === 0 ? (
                <div className="text-center text-gray-500 py-8">
                  <Star size={48} className="mx-auto mb-2 text-gray-300" />
                  <p>No templates saved yet</p>
                  <p className="text-sm">Create your first template to get started</p>
                </div>
              ) : (
                templates.map(template => (
                  <div key={template.id} className="border border-gray-200 rounded-lg p-3 hover:border-blue-300 transition-colors">
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex-1">
                        <h5 className="font-medium text-gray-800">{template.name}</h5>
                        {template.description && (
                          <p className="text-sm text-gray-600 mt-1">{template.description}</p>
                        )}
                        <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                          <span>{template.node_count} nodes</span>
                          <span>{new Date(template.created_at).toLocaleDateString()}</span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => loadTemplate(template)}
                        className="flex items-center gap-1 px-3 py-1 text-sm bg-green-100 text-green-700 rounded hover:bg-green-200 transition-colors"
                      >
                        <Copy size={12} />
                        Use Template
                      </button>
                      <button
                        onClick={() => deleteTemplate(template.id)}
                        className="flex items-center gap-1 px-3 py-1 text-sm bg-red-100 text-red-700 rounded hover:bg-red-200 transition-colors"
                      >
                        <Trash2 size={12} />
                        Delete
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TemplateManager;
